import {
  Badge,
  Button,
  Checkbox,
  Col,
  Popover,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd"
import {
  FilterFilled,
  FilterOutlined,
  ScissorOutlined,
  VerticalAlignMiddleOutlined,
} from "@ant-design/icons"
import { ASAPOrder, OrderAndPrefix } from "../../../Orders/Helpers/OrderAndPrefix"
import moment from "moment"
import { useEffect, useState } from "react"
import MakePriorityJob from "../Action/MakePriorityJob"
import "../../../../styles/OrderTable.css"
import { OrderLocation } from "../../../Orders/OrderLocation"
import DeleteOrder from "../Action/DeleteOrder"

const { Text } = Typography

export default function MachinesOrderTable({ activeKey, tableData, loading }) {
  const [filteredData, setFilteredData] = useState([])
  const [filterLoading, setFilterLoading] = useState(false)

  // Object to manage visibility of each column
  const [visibleColumns, setVisibleColumns] = useState({
    orderNumber: true,
    created: true,
    client: true,
    product: true,
    quantity: true,
    dieCut: true,
    material: true,
    height: true,
    width: true,
    plate: true,
    technical: true,
    manager: true,
    projectedMeters: true,
    materialWidth: true,
    action: true,
    location: true,
  })
  const [ellipsisColumns, setEllipsisColumns] = useState({
    orderNumber: true,
    created: true,
    client: true,
    product: true,
    quantity: true,
    dieCut: true,
    material: true,
    height: true,
    width: true,
    plate: true,
    technical: true,
    manager: true,
    projectedMeters: true,
    materialWidth: true,
    action: true,
  })

  const [filterActive, setFilterActive] = useState(false)

  useEffect(() => {
    setFilterLoading(true) // Begin showing loading indicator

    // Simulated asynchronous operation to mimic complex filtering
    const timer = setTimeout(() => {
      const filtered = tableData.filter((item) => {
        switch (activeKey) {
          case 2:
            return item.orderStatus === 3
          case 3:
            return item.orderStatus === 4
          case 4:
            return item.orderStatus === 5
          default:
            return true // No filtering or specific case for default
        }
      })

      setFilteredData(filtered)
      setFilterLoading(false) // Stop showing loading indicator
    }, 150) // Adjust the delay as necessary for your use case

    // Cleanup the timeout if the component unmounts or if activeKey or tableData changes before the timeout completes
    return () => clearTimeout(timer)
  }, [activeKey, tableData]) // Depend on activeKey and tableData so this runs anytime they change

  let clientFIlters =
    filteredData &&
    filteredData.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    filteredData &&
    filteredData.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  let materialFilter =
    filteredData &&
    filteredData.reduce((accumulator, item) => {
      const materialName = item.selectedProduct.material
      const existingItem = accumulator.find((filterItem) => filterItem.text === materialName)

      if (existingItem) {
        existingItem.value = materialName
      } else {
        accumulator.push({
          text: materialName,
          value: materialName,
        })
      }

      return accumulator
    }, [])
  let userFilter =
    filteredData &&
    filteredData.reduce((accumulator, item) => {
      const userName = item.user
      const existingItem = accumulator.find((filterItem) => filterItem.text === userName)

      if (existingItem) {
        existingItem.value = userName
      } else {
        accumulator.push({
          text: userName,
          value: userName,
        })
      }

      return accumulator
    }, [])

  const tableColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      filterTitle: "Order Number",
      key: "orderNumber",
      width: 120,
      ellipsis: ellipsisColumns.orderNumber,
      align: "center",
      sorter: (a, b) => a.justOrderNumber - b.justOrderNumber,
      render: (value, record) => (
        <Space>
          {ASAPOrder(record)}
          {OrderAndPrefix(record)}
        </Space>
      ),
    },
    {
      title: "Location",
      dataIndex: "orderStatus",
      align: "center",
      key: "location",
      filterTitle: "locations",
      render: (value) => <Badge status="processing" text={OrderLocation(value)} />,
      onFilter: (value, record) => record.orderStatus === value,
      filterSearch: true,
    },
    {
      title: "Created",
      dataIndex: "created",
      align: "center",
      key: "created",
      width: 100,
      ellipsis: ellipsisColumns.created,
      filterTitle: "Creation Date",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => (
        <>
          <Tag>{moment.unix(value.seconds).format("DD.MM.YYYY")}</Tag>
        </>
      ),
    },
    {
      title: "Due D.",
      dataIndex: "orderDueDate",
      align: "center",
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      key: "created",
      render: (value, record) => {
        const dueDate = moment.unix(value.seconds)
        const isOverdue = dueDate.isBefore(moment(), "day")
        if (isOverdue) {
          return <Tag color="red"> {dueDate.format("DD.MM.YYYY")}</Tag>
        } else {
          return <Tag color="blue">{dueDate.format("DD.MM.YYYY")}</Tag>
        }
      },
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      ellipsis: ellipsisColumns.client,
      key: "client",
      filterTitle: "Clients",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      sorter: (a, b) => {
        const clientA = a.client?.client || ""
        const clientB = b.client?.client || ""
        return clientA.localeCompare(clientB)
      },
      filterSearch: true,
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      ellipsis: ellipsisColumns.product,
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      filterTitle: "Products",
      key: "product",

      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Manager",
      dataIndex: ["user"],
      align: "center",
      key: "manager",
      filterTitle: "Managers",
      filters: userFilter,
      ellipsis: ellipsisColumns.manager,
      filterSearch: true,
      onFilter: (value, record) => record.user === value,
      width: "7%",
    },
    {
      title: "Quantity",
      dataIndex: "labelCount",
      align: "center",
      filterTitle: "Created Amount",
      key: "quantity",
      ellipsis: ellipsisColumns.quantity,
      width: "7%",
      sorter: (a, b) => a.labelCount - b.labelCount,
    },
    {
      title: "Die-cut",
      dataIndex: ["selectedProduct", "knifeCode"],
      align: "center",
      filterTitle: "Die Cut's",
      key: "dieCut",
      ellipsis: ellipsisColumns.dieCut,
    },

    {
      title: "Width",
      dataIndex: ["selectedProduct", "labelWidth"],
      align: "center",
      width: "5%",
      filterTitle: "Label's Width",
      key: "width",
      ellipsis: ellipsisColumns.width,
    },
    {
      title: "Height",
      dataIndex: ["selectedProduct", "labelHeight"],
      align: "center",
      width: "5%",
      filterTitle: "Label's Height",
      key: "height",
      ellipsis: ellipsisColumns.height,
    },
    {
      title: "M.Width",
      dataIndex: ["selectedProduct", "formatWidth"],
      align: "center",
      key: "materialWidth",
      ellipsis: ellipsisColumns.materialWidth,
      filterTitle: "Material Width",
      sorter: (a, b) => a.selectedProduct.formatWidth - b.selectedProduct.formatWidth,
      render: (value) => <Text>{value}mm</Text>,
    },
    {
      title: "Proj. M.",
      dataIndex: ["projectedMeters"],
      align: "center",
      key: "projectedMeters",
      filterTitle: "Projected Meters",
      ellipsis: ellipsisColumns.projectedMeters,
      width: "7%",
      render: (value, record) => <Text>{value}m</Text>,
    },
    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "center",
      filters: materialFilter,
      onFilter: (value, record) => record.selectedProduct.material.startsWith(value),
      filterSearch: true,
      key: "material",
      filterTitle: "Materials",
      ellipsis: ellipsisColumns.material,
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },

    {
      title: "Plate",
      dataIndex: ["selectedPlate", "code"],
      align: "center",
      filterTitle: "Plates",
      key: "plate",
      ellipsis: ellipsisColumns.plate,
      render: (item) => {
        if (item) {
          return (
            <Tooltip placement="topLeft" title={item}>
              {item}
            </Tooltip>
          )
        } else {
          return <ScissorOutlined style={{ color: "#f5222d" }} />
        }
      },
    },
    {
      title: "Action",
      align: "center",
      alwaysVisible: true,
      key: "action",
      width: 70,
      fixed: "right",
      className: "action-column",
      style: { background: "black" },
      render: (value, record) => {
        return (
          <>
            <Space>
              <MakePriorityJob data={record} />
              <DeleteOrder data={record} icon={true} />
            </Space>
          </>
        )
      },
    },
  ]

  //handles the action for visibility change
  const handleVisibilityChange = (key, checked) => {
    setVisibleColumns((prev) => {
      const updatedVisibleColumns = { ...prev, [key]: checked }

      // Check if any columns are hidden to set filter active state
      const isAnyColumnHidden = Object.values(updatedVisibleColumns).includes(false)
      setFilterActive(isAnyColumnHidden)

      return updatedVisibleColumns
    })
  }

  //ellipsis
  const handleEllipsis = (key, checked) => {
    setEllipsisColumns((prev) => ({
      ...prev,
      [key]: checked,
    }))
  }

  //controls for handling the filter
  const columnVisibilityControls = (
    <div style={{ margin: -8, background: "white", borderRadius: "5px" }}>
      <Row>
        <Col span={12}>
          <Space direction="vertical" style={{ padding: 15 }}>
            <Tag>Show/Hide</Tag>
            {tableColumns
              .filter((col) => !col.alwaysVisible)
              .map((col) => (
                <Checkbox
                  key={col.key}
                  checked={visibleColumns[col.key]}
                  onChange={(e) => handleVisibilityChange(col.key, e.target.checked)}>
                  {col.filterTitle}
                </Checkbox>
              ))}
          </Space>
        </Col>
        <Col span={12}>
          <Space direction="vertical" style={{ padding: 15 }}>
            <Tag>
              Shorten Columns <VerticalAlignMiddleOutlined rotate={90} />
            </Tag>
            {tableColumns
              .filter((col) => !col.alwaysVisible)
              .map((col) => (
                <Checkbox
                  key={col.key}
                  checked={ellipsisColumns[col.key]}
                  onChange={(e) => handleEllipsis(col.key, e.target.checked)}>
                  {col.filterTitle}
                </Checkbox>
              ))}
          </Space>
        </Col>
      </Row>
    </div>
  )

  const filteredColumns = tableColumns.filter((column) => visibleColumns[column.key])

  // Right before returning your component's JSX
  const computedColumns = filteredColumns.map((column) => ({
    ...column,
    ellipsis: ellipsisColumns[column.key] ?? column.ellipsis, // Apply dynamic ellipsis, with fallback to original ellipsis state
  }))

  return (
    <div>
      <Popover
        color="#597ef7"
        placement="bottomLeft"
        trigger="click"
        content={columnVisibilityControls}>
        <Button
          type={filterActive ? "primary" : "dashed"}
          icon={filterActive ? <FilterFilled /> : <FilterOutlined />}>
          <Space>
            Filter Columns
            {filterActive ? (
              <Text style={{ color: "lightgray" }} italic>
                (active)
              </Text>
            ) : (
              ""
            )}
          </Space>
        </Button>
      </Popover>
      <Table
        rowKey={(record) => record.id}
        loading={loading || filterLoading}
        dataSource={filteredData}
        columns={computedColumns}
        size="small"
        bordered
        style={{ marginTop: 5 }}
        rowClassName={(record) => (record.priorityOrder ? "red-background" : "")}
        scroll={{
          x: 2000,
        }}
        pagination={{ pageSize: 25, showSizeChanger: true, size: "small" }}
      />
    </div>
  )
}
